import { ConnectionsQuestionResponse } from '../types';

/**
 * Shared Positive / Negative / RNA bucketing for the question-response data.
 * Used by both the compact QuestionSentimentSummary (Dashboard) and the full
 * per-question QuestionResponseBars (Insights) so they always agree.
 *
 * Tune the rule here if the real response_option_text / score scale differs
 * (verify against alpha data via bb):
 *   - RNA      = the "rather not answer" option (text match) or a null score.
 *   - Positive = favourable options (score > 0).
 *   - Negative = unfavourable options (score <= 0, non-RNA).
 */
export const POS_COLOR = '#1e7d32';
export const NEG_COLOR = '#c62828';
export const RNA_COLOR = '#b76e00';

export type Bucket = 'positive' | 'negative' | 'rna';

/**
 * A row counts toward the sentiment bar ONLY if it is a 1-5 Likert option.
 * The andes view also carries non-sentiment question types (free-text, multi-select,
 * frequency/demographic) whose options have a null score -- those are excluded entirely
 * so they neither pollute the Neutral bucket nor show up as bars.
 */
const isSentiment = (r: ConnectionsQuestionResponse): boolean => r.score != null && r.score >= 1 && r.score <= 5;

export const bucketOf = (r: ConnectionsQuestionResponse): Bucket => {
  // Only called for sentiment rows (see rollupByQuestion). Standard Connections favorability:
  //   Positive = top-2-box (4-5), Negative = bottom-2-box (1-2), middle bucket = neutral (3).
  if ((r.score as number) >= 4) return 'positive';
  if ((r.score as number) <= 2) return 'negative';
  return 'rna'; // score === 3 -> neutral ("Neither Agree nor Disagree" / "Moderately Satisfied")
};

export interface QuestionRollup {
  questionId: string;
  questionText: string;
  positive: number;
  negative: number;
  rna: number;
  total: number;
}

/** Roll per-(question, response-option) rows up into per-question P/N/RNA totals. */
export const rollupByQuestion = (data: ConnectionsQuestionResponse[] | null | undefined): QuestionRollup[] => {
  const byQ = new Map<string, QuestionRollup>();
  (data || []).forEach((r) => {
    if (!isSentiment(r)) return; // drop free-text / multi-select / demographic (null-score) rows
    const q =
      byQ.get(r.questionId) ||
      ({
        questionId: r.questionId,
        questionText: r.questionText || r.questionId,
        positive: 0,
        negative: 0,
        rna: 0,
        total: 0
      } as QuestionRollup);
    const count = r.responseCount ?? 0;
    q[bucketOf(r)] += count;
    q.total += count;
    byQ.set(r.questionId, q);
  });
  return Array.from(byQ.values()).filter((q) => q.total > 0);
};
